import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { BiSolidInbox } from 'react-icons/bi';
import {
  createOtherExpense,
  deleteOtherExpense,
  listOtherExpenses,
  updateOtherExpense,
} from '@/api/finance.api';
import { ApiError } from '@/api/errors';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { FormField } from '@/components/form/FormField';
import {
  DataTable,
  type ActiveFilterChip,
  type DataTableColumn,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { formatCurrencyEGP, formatDateOnly } from '@/lib/format';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { OTHER_EXPENSE_CATEGORIES } from '@/types/shared';
import type { OtherExpenseCategory, OtherExpenseRow, PaginatedOtherExpenses } from '@/types/shared';
import './FinanceExpensesTab.css';

interface ExpenseFormState {
  category: OtherExpenseCategory;
  amount: string;
  date: string;
  note: string;
}

interface ExpenseFieldErrors {
  category?: string;
  amount?: string;
  date?: string;
  note?: string;
}

const EMPTY_FORM: ExpenseFormState = {
  category: OTHER_EXPENSE_CATEGORIES[0],
  amount: '',
  date: '',
  note: '',
};

function toFormState(row: OtherExpenseRow): ExpenseFormState {
  return {
    category: row.category,
    amount: row.amount,
    date: row.date,
    note: row.note ?? '',
  };
}

/**
 * Other Expenses tab (`/finance`, tab 2, 17-finance-ux.md §3.6) — a standard paginated list-page
 * pattern (page header + `DataTable` with toolbar/`FilterDrawer`/chips), CRUD wired to the T124
 * `/finance/expenses` endpoints. Both Admin and Accounts Officer have full CRUD here (the
 * `FINANCE_READ_ROLES` tier owns config READ + expenses CRUD per DEC-FIN1 — no narrower in-tab
 * role check needed beyond the page-level `RoleGuard`).
 */
export function FinanceExpensesTab() {
  const { t, i18n } = useTranslation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [categoryFilter, setCategoryFilter] = useState<OtherExpenseCategory | ''>('');
  const [fromFilter, setFromFilter] = useState('');
  const [toFilter, setToFilter] = useState('');

  const [data, setData] = useState<PaginatedOtherExpenses | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [panel, setPanel] = useState<
    { mode: 'create' } | { mode: 'edit'; row: OtherExpenseRow } | null
  >(null);
  const [form, setForm] = useState<ExpenseFormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<ExpenseFieldErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [confirmDeleteRow, setConfirmDeleteRow] = useState<OtherExpenseRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Any filter change re-starts pagination at page 1.
  useEffect(() => {
    setPage(1);
  }, [categoryFilter, fromFilter, toFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listOtherExpenses({
      page,
      pageSize,
      category: categoryFilter === '' ? undefined : categoryFilter,
      from: fromFilter || undefined,
      to: toFilter || undefined,
    })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setError(t('finance.expenses.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, categoryFilter, fromFilter, toFilter, reloadToken, t]);

  function refetch() {
    setReloadToken((v) => v + 1);
  }

  const activeFilterChips: ActiveFilterChip[] = [
    categoryFilter !== '' && {
      key: 'category',
      label: t('finance.expenses.filters.category'),
      valueLabel: t(`finance.expenses.categories.${categoryFilter}`),
      onRemove: () => setCategoryFilter(''),
    },
    (fromFilter || toFilter) && {
      key: 'date',
      label: t('finance.expenses.filters.date'),
      valueLabel: `${fromFilter ? formatDateOnly(fromFilter, i18n.language) : '…'} – ${
        toFilter ? formatDateOnly(toFilter, i18n.language) : '…'
      }`,
      onRemove: () => {
        setFromFilter('');
        setToFilter('');
      },
    },
  ].filter(Boolean) as ActiveFilterChip[];

  function clearFilters() {
    setCategoryFilter('');
    setFromFilter('');
    setToFilter('');
  }

  function openCreatePanel() {
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setSaveError(null);
    setPanel({ mode: 'create' });
  }

  function openEditPanel(row: OtherExpenseRow) {
    setForm(toFormState(row));
    setFieldErrors({});
    setSaveError(null);
    setPanel({ mode: 'edit', row });
  }

  function closePanel() {
    setPanel(null);
    setFieldErrors({});
    setSaveError(null);
  }

  function validate(): ExpenseFieldErrors {
    const errors: ExpenseFieldErrors = {};
    const amountNum = Number(form.amount);
    if (form.amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
      errors.amount = t('finance.expenses.form.errors.amountInvalid');
    }
    if (!form.date) {
      errors.date = t('finance.expenses.form.errors.dateRequired');
    }
    return errors;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!panel) return;
    setSaveError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const payload = {
        category: form.category,
        amount: form.amount.trim(),
        date: form.date,
        note: form.note.trim() ? form.note.trim() : null,
      };
      if (panel.mode === 'create') {
        await createOtherExpense(payload);
        showSuccessToast(t('finance.expenses.form.createSuccess'));
      } else {
        await updateOtherExpense(panel.row.id, payload);
        showSuccessToast(t('finance.expenses.form.updateSuccess'));
      }
      closePanel();
      refetch();
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const errs: ExpenseFieldErrors = {};
      for (const detail of apiError.details) {
        if (detail.field === 'category') errs.category = detail.message;
        if (detail.field === 'amount') errs.amount = detail.message;
        if (detail.field === 'date') errs.date = detail.message;
        if (detail.field === 'note') errs.note = detail.message;
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
      } else if (apiError.code === 'EXPENSE_ALREADY_DELETED') {
        showErrorToast(t('finance.expenses.alreadyDeletedError'));
        closePanel();
        refetch();
      } else {
        setSaveError(
          apiError.code === 'NETWORK_ERROR'
            ? t('finance.expenses.form.networkError')
            : apiError.message || t('finance.expenses.form.genericError'),
        );
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmDelete() {
    if (!confirmDeleteRow) return;
    setDeleting(true);
    try {
      await deleteOtherExpense(confirmDeleteRow.id);
      showSuccessToast(t('finance.expenses.deleteSuccess'));
      setConfirmDeleteRow(null);
      refetch();
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      showErrorToast(
        apiError.code === 'EXPENSE_ALREADY_DELETED'
          ? t('finance.expenses.alreadyDeletedError')
          : t('toast.genericError'),
      );
      setConfirmDeleteRow(null);
      refetch();
    } finally {
      setDeleting(false);
    }
  }

  const columns: DataTableColumn<OtherExpenseRow>[] = [
    {
      key: 'date',
      header: t('finance.expenses.columns.date'),
      toggleable: false,
      render: (row) => <bdi>{formatDateOnly(row.date, i18n.language)}</bdi>,
    },
    {
      key: 'category',
      header: t('finance.expenses.columns.category'),
      render: (row) => (
        <StatusChip label={t(`finance.expenses.categories.${row.category}`)} tone="neutral" />
      ),
    },
    {
      key: 'amount',
      header: t('finance.expenses.columns.amount'),
      cellClassName: 'fa-ltr-isolate',
      render: (row) => <bdi>{formatCurrencyEGP(Number(row.amount), i18n.language)}</bdi>,
    },
    {
      key: 'note',
      header: t('finance.expenses.columns.note'),
      render: (row) => row.note || '—',
    },
    {
      key: 'actions',
      header: t('finance.expenses.columns.actions'),
      toggleable: false,
      cellClassName: 'fa-finance-expenses__actions-cell',
      render: (row) => (
        <div className="fa-finance-expenses__row-actions">
          <button
            type="button"
            className="fa-btn fa-btn--ghost"
            aria-label={t('finance.expenses.editAriaLabel', {
              date: formatDateOnly(row.date, i18n.language),
            })}
            onClick={() => openEditPanel(row)}
          >
            {t('finance.expenses.editButton')}
          </button>
          <button
            type="button"
            className="fa-btn fa-btn--secondary"
            aria-label={t('finance.expenses.deleteAriaLabel', {
              date: formatDateOnly(row.date, i18n.language),
            })}
            onClick={() => setConfirmDeleteRow(row)}
          >
            {t('finance.expenses.deleteButton')}
          </button>
        </div>
      ),
    },
  ];

  const hasActiveFilters = activeFilterChips.length > 0;
  const emptyState = hasActiveFilters ? (
    <EmptyState
      icon={BiSolidInbox}
      title={t('finance.expenses.empty.noMatchesTitle')}
      hint={t('finance.expenses.empty.noMatchesHint')}
      action={
        <button type="button" className="fa-btn fa-btn--secondary" onClick={clearFilters}>
          {t('finance.expenses.empty.clearFilters')}
        </button>
      }
    />
  ) : (
    <EmptyState
      icon={BiSolidInbox}
      title={t('finance.expenses.empty.title')}
      hint={t('finance.expenses.empty.hint')}
      action={
        <button type="button" className="fa-btn fa-btn--primary" onClick={openCreatePanel}>
          {t('finance.expenses.addButton')}
        </button>
      }
    />
  );

  return (
    <div className="fa-finance-expenses">
      <div className="fa-finance-expenses__header">
        <div>
          <h2 className="fa-finance-expenses__title">{t('finance.expenses.title')}</h2>
          <p className="fa-finance-expenses__subtitle">{t('finance.expenses.subtitle')}</p>
        </div>
        <button type="button" className="fa-btn fa-btn--primary" onClick={openCreatePanel}>
          {t('finance.expenses.addButton')}
        </button>
      </div>

      {panel && (
        <form className="fa-form-section" onSubmit={handleSubmit} noValidate>
          <h3 className="fa-form-section__title">
            {panel.mode === 'create'
              ? t('finance.expenses.form.addTitle')
              : t('finance.expenses.form.editTitle')}
          </h3>
          <div className="fa-field-row">
            <FormField
              id="expense-category"
              label={t('finance.expenses.form.category')}
              required
              error={fieldErrors.category}
            >
              <select
                value={form.category}
                disabled={saving}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    category: e.target.value as OtherExpenseCategory,
                  }));
                  setFieldErrors((prev) => ({ ...prev, category: undefined }));
                }}
              >
                {OTHER_EXPENSE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`finance.expenses.categories.${category}`)}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField
              id="expense-amount"
              label={t('finance.expenses.form.amount')}
              required
              error={fieldErrors.amount}
            >
              <input
                type="number"
                min="0"
                step="0.01"
                className="fa-ltr-isolate"
                value={form.amount}
                disabled={saving}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, amount: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, amount: undefined }));
                }}
              />
            </FormField>
          </div>
          <div className="fa-field-row">
            <FormField
              id="expense-date"
              label={t('finance.expenses.form.date')}
              required
              error={fieldErrors.date}
            >
              <input
                type="date"
                value={form.date}
                disabled={saving}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, date: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, date: undefined }));
                }}
              />
            </FormField>
            <FormField
              id="expense-note"
              label={t('finance.expenses.form.note')}
              error={fieldErrors.note}
            >
              <textarea
                value={form.note}
                disabled={saving}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, note: e.target.value }));
                  setFieldErrors((prev) => ({ ...prev, note: undefined }));
                }}
              />
            </FormField>
          </div>
          <div className="fa-form-actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={saving}
              onClick={closePanel}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="fa-btn fa-btn--primary" disabled={saving}>
              {saving
                ? t('finance.expenses.form.savingButton')
                : t('finance.expenses.form.saveButton')}
            </button>
          </div>
          {saveError && (
            <p className="fa-field__error" role="alert">
              {saveError}
            </p>
          )}
        </form>
      )}

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        caption={t('finance.expenses.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={refetch}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        activeFilters={activeFilterChips}
        onClearAllFilters={clearFilters}
        filtersDrawerTitle={t('finance.expenses.filters.groupLabel')}
        filtersSlot={
          <div className="fa-finance-expenses__filters-group">
            <label className="fa-finance-expenses__filter">
              <span className="fa-finance-expenses__filter-label">
                {t('finance.expenses.filters.category')}
              </span>
              <select
                value={categoryFilter}
                onChange={(e) =>
                  setCategoryFilter(
                    e.target.value === '' ? '' : (e.target.value as OtherExpenseCategory),
                  )
                }
              >
                <option value="">{t('finance.expenses.filters.allCategories')}</option>
                {OTHER_EXPENSE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {t(`finance.expenses.categories.${category}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="fa-finance-expenses__filter">
              <span className="fa-finance-expenses__filter-label">
                {t('finance.expenses.filters.from')}
              </span>
              <input
                type="date"
                value={fromFilter}
                onChange={(e) => setFromFilter(e.target.value)}
              />
            </label>
            <label className="fa-finance-expenses__filter">
              <span className="fa-finance-expenses__filter-label">
                {t('finance.expenses.filters.to')}
              </span>
              <input type="date" value={toFilter} onChange={(e) => setToFilter(e.target.value)} />
            </label>
          </div>
        }
        emptyState={emptyState}
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteRow)}
        title={t('finance.expenses.deleteConfirm.title')}
        description={confirmDeleteRow ? t('finance.expenses.deleteConfirm.body') : undefined}
        confirmLabel={t('finance.expenses.deleteConfirm.confirmButton')}
        cancelLabel={t('common.cancel')}
        destructive
        confirmDisabled={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteRow(null)}
      />
    </div>
  );
}
